import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    EditorialNav,
    EditorialFooter,
    Eyebrow,
    mono,
    serif,
    hairline,
    ivory,
    bodyText,
    muted,
    subtle,
} from './landing/EditorialShell';
import { useT } from '../i18n/useT';
import '../assets/scss/landing-v2.scss';

const LoginPage: React.FC = () => {
    const t = useT();
    const [email, setEmail] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user, loading: authLoading, signInWithMagicLink } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (authLoading || !user) return;
        navigate('/dashboard');
    }, [user, authLoading, navigate]);

    const handleMagicLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');
        setSuccess(false);

        try {
            const { error } = await signInWithMagicLink(email);

            if (error) {
                setErrorMsg(error.message);
            } else {
                setSuccess(true);
            }
        } catch (err: any) {
            setErrorMsg(err?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="lp2"
            style={{
                background: '#0a0a0a',
                color: ivory,
                fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <EditorialNav />
            <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <section
                    style={{
                        padding: 'clamp(56px, 9vw, 120px) clamp(20px, 4vw, 40px)',
                        width: '100%',
                    }}
                >
                    <div style={{ maxWidth: 460, margin: '0 auto', position: 'relative' }}>
                        <div
                            aria-hidden
                            style={{
                                position: 'absolute',
                                top: -120,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 720,
                                height: 480,
                                pointerEvents: 'none',
                                background:
                                    'radial-gradient(ellipse at 50% 50%, rgba(239,68,68,0.10) 0%, transparent 65%)',
                            }}
                        />

                        <div style={{ textAlign: 'center', marginBottom: 36, position: 'relative' }}>
                            <Eyebrow color="#ef4444">{t('login.eyebrow')}</Eyebrow>
                            <h1
                                style={{
                                    fontSize: 'clamp(34px, 5vw, 52px)',
                                    fontWeight: 200,
                                    color: ivory,
                                    letterSpacing: '-0.035em',
                                    lineHeight: 1.05,
                                    margin: '14px 0 14px',
                                }}
                            >
                                {t('login.title.lead')}{' '}
                                <span
                                    style={{
                                        color: muted,
                                        fontStyle: 'italic',
                                        fontFamily: serif,
                                        fontWeight: 300,
                                    }}
                                >
                                    {t('login.title.italic')}
                                </span>
                            </h1>
                            <p
                                style={{
                                    fontSize: 15,
                                    color: bodyText,
                                    lineHeight: 1.6,
                                    margin: '0 auto',
                                    maxWidth: 380,
                                }}
                            >
                                {t('login.subtitle')}
                            </p>
                        </div>

                        <div
                            style={{
                                background: '#0c0b0a',
                                border: hairline,
                                borderRadius: 4,
                                padding: 'clamp(24px, 4vw, 32px)',
                                position: 'relative',
                            }}
                        >
                            <form onSubmit={handleMagicLink} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                                <label htmlFor="email-input" style={{ display: 'block' }}>
                                    <span
                                        style={{
                                            display: 'block',
                                            fontSize: 10,
                                            color: muted,
                                            letterSpacing: '0.18em',
                                            fontFamily: mono,
                                            fontWeight: 500,
                                            marginBottom: 8,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {t('login.label.email')}
                                    </span>
                                    <input
                                        id="email-input"
                                        type="email"
                                        placeholder={t('login.placeholder.email')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={loading || success}
                                        style={{
                                            width: '100%',
                                            background: 'rgba(232, 230, 224, 0.03)',
                                            border: '1px solid rgba(232, 230, 224, 0.1)',
                                            borderRadius: 4,
                                            padding: '13px 14px',
                                            color: ivory,
                                            fontFamily: 'inherit',
                                            fontSize: 14,
                                            outline: 'none',
                                            opacity: success ? 0.6 : 1,
                                        }}
                                    />
                                </label>

                                <button
                                    type="submit"
                                    disabled={loading || success}
                                    style={{
                                        background: success ? 'rgba(132, 204, 22, 0.12)' : '#ef4444',
                                        color: success ? '#84cc16' : ivory,
                                        border: success ? '1px solid rgba(132, 204, 22, 0.4)' : 'none',
                                        padding: '14px 22px',
                                        borderRadius: 4,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: loading || success ? 'default' : 'pointer',
                                        fontFamily: mono,
                                        letterSpacing: '0.1em',
                                        boxShadow: success ? 'none' : '0 0 28px rgba(239, 68, 68, 0.35)',
                                        opacity: loading ? 0.7 : 1,
                                        transition: 'background 0.3s ease, color 0.3s ease',
                                    }}
                                >
                                    {(() => {
                                        if (success) return `✓ ${t('login.message.success')}`;
                                        if (loading) return t('login.button.sending');

                                        return t('login.button.send');
                                    })()}
                                </button>

                                {errorMsg && (
                                    <div
                                        role="alert"
                                        style={{
                                            fontSize: 13,
                                            color: '#ef4444',
                                            background: 'rgba(239, 68, 68, 0.08)',
                                            border: '1px solid rgba(239, 68, 68, 0.25)',
                                            borderRadius: 4,
                                            padding: '10px 14px',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {errorMsg}
                                    </div>
                                )}
                            </form>
                        </div>

                        <p
                            style={{
                                marginTop: 22,
                                fontSize: 11,
                                color: subtle,
                                textAlign: 'center',
                                fontFamily: mono,
                                letterSpacing: '0.12em',
                                lineHeight: 1.6,
                            }}
                        >
                            {t('login.footer.note')}
                        </p>
                    </div>
                </section>
            </main>
            <EditorialFooter />
        </div>
    );
};

export default LoginPage;
